import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LockClockIcon from '@mui/icons-material/LockClock';
import { ReactElement } from 'react';
import { useRecordContext, WithRecord } from 'react-admin';

import { GetInstrumentOneResponse, isQs99CutoffIsin } from '@Common/types/bo/instruments';

import { ShowWithNavigation, VerticalTab, VerticalTabsLayout } from '@sh4res/bo-common/components';
import { InstrumentDocumentsCreate } from '@sh4res/bo-common/components/Instruments/documents/InstrumentDocumentsCreate';
import { InstrumentDocumentsSection } from '@sh4res/bo-common/components/Instruments/sections';
import { InstrumentCutoffDatesSection } from '@sh4res/bo-common/components/Instruments/sections/CutoffDates';
import { InstrumentDetailsSection } from '@sh4res/bo-common/components/Instruments/sections/Details';
import { InstrumentScheduledEditCreateButton } from '@sh4res/bo-common/components/Instruments/sections/ScheduledEdits/components/InstrumentScheduledEditCreateButton';
import { InstrumentScheduledEditsSection } from '@sh4res/bo-common/components/Instruments/sections/ScheduledEdits/index';
import { useSetInstrumentLocation } from '@sh4res/bo-common/components/Instruments/useSetInstrumentLocation';
import { useCanAccess } from '@sh4res/bo-common/hooks';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const InstrumentLocationSetter = () => {
  const record = useRecordContext<GetInstrumentOneResponse>();
  useSetInstrumentLocation(record?.type);
  return null;
};

export const InstrumentShow = ({
  InstrumentEdit,
  CutoffDatesEdit,
}: {
  InstrumentEdit?: ReactElement;
  CutoffDatesEdit?: ReactElement;
}) => {
  const canEditInstrument = useCanAccess({
    resource: 'instruments',
    action: 'edit',
  });

  const canCreateInstrumentDocument = useCanAccess({
    resource: 'instruments/documents',
    action: 'create',
  });

  return (
    <ShowWithNavigation
      actions={false}
      title={
        <WithRecord
          render={(record: GetInstrumentOneResponse) => record.short_name ?? record.symbol}
        />
      }
      logs={{
        resource: 'instrument',
      }}
      comments={
        isBoExternal()
          ? undefined
          : {
              source: 'id',
              target: 'instrument_id',
            }
      }
      sx={{ height: '100%' }}
      navigateTo={() => -1}>
      <InstrumentLocationSetter />
      <WithRecord<GetInstrumentOneResponse>
        render={(record) => (
          <VerticalTabsLayout>
            <VerticalTab
              button={canEditInstrument && InstrumentEdit ? InstrumentEdit : <></>}
              icon={<AssignmentIndIcon />}
              label="resources.bo_common.instruments.tabs.details">
              <InstrumentDetailsSection />
            </VerticalTab>
            <VerticalTab
              button={
                canCreateInstrumentDocument && record.type !== 'crypto' && !isBoExternal() ? (
                  <InstrumentDocumentsCreate
                    resource="instruments/documents"
                    icon={<FileUploadIcon />}
                    label="resources.bo_common.instruments.documents.create.button"
                    instrument={{ id: record.id, type: record.type }}
                  />
                ) : (
                  <></>
                )
              }
              icon={<ArticleIcon />}
              path="documents"
              label="resources.bo_common.instruments.tabs.documents">
              <InstrumentDocumentsSection />
            </VerticalTab>
            {record.type === 'mutual-fund' && isQs99CutoffIsin(record.isin) ? (
              <VerticalTab
                button={CutoffDatesEdit && !isBoExternal() ? CutoffDatesEdit : <></>}
                icon={<LockClockIcon />}
                path="cutoff_dates"
                label="resources.bo_common.instruments.tabs.cutoff_dates">
                <InstrumentCutoffDatesSection />
              </VerticalTab>
            ) : null}
            {!isBoExternal() && (
              <VerticalTab
                button={
                  <InstrumentScheduledEditCreateButton
                    instrument={record}
                    data-testid="BoExternalInstrumentScheduledEditsCreateButton"
                  />
                }
                icon={<CalendarMonthIcon />}
                path="schedule"
                label="resources.bo_common.instruments.tabs.schedule">
                <InstrumentScheduledEditsSection />
              </VerticalTab>
            )}
          </VerticalTabsLayout>
        )}
      />
    </ShowWithNavigation>
  );
};
